import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { getWindowSafe } from '@change-corgi/core/window';

import { copyToClipboard } from 'src/app/shared/utils/clipboard';

import { useShareUrlWithUtmParams } from '../../utils/getShareUrlWithUtmParams';

import type { ActionsReturnType, GroupSharingHookProps } from './types';

const TRACKING_EVENT_NAME = 'share_petition';
const SHARING_EVENT_PREFIX = 'ready_to_share_reddit_group_post';
// eslint-disable-next-line max-lines-per-function
export function useRedditGroupPostSharing(props: GroupSharingHookProps): ActionsReturnType {
	const track = useTracking();
	const i18n = useI18n();
	const window = getWindowSafe();

	const {
		recommendedGroup,
		message,
		petitionTitle = '',
		shareUrl = '',
		trackingProps: { context, viewContext },
	} = props;

	const shareUrlWithUtmParams = useShareUrlWithUtmParams(shareUrl, 'reddit_group');

	const primaryAction = useCallback(() => {
		void copyToClipboard(message, i18n);

		const trackingData = {
			context,
			location: 'reddit_group',
			tab_at_share: viewContext,
			app_available: false,
			content: 'post',
		};
		void track(TRACKING_EVENT_NAME, trackingData);

		if (window) {
			const redditUrl = `https://www.reddit.com/${recommendedGroup?.name}/submit?title=${encodeURIComponent(petitionTitle)}&url=${encodeURIComponent(shareUrlWithUtmParams || shareUrl)}`;
			window.open(redditUrl, '_blank');
			void track(`${SHARING_EVENT_PREFIX}_success`, trackingData);
		} else {
			void track(`${SHARING_EVENT_PREFIX}_failure`, trackingData);
		}
	}, [
		context,
		i18n,
		message,
		petitionTitle,
		recommendedGroup?.name,
		shareUrl,
		shareUrlWithUtmParams,
		track,
		viewContext,
		window,
	]);

	const secondaryAction = useCallback(() => {
		const trackingData = {
			context,
			location: 'reddit_group',
			tab_at_share: viewContext,
			app_available: false,
			content: 'view_group',
		};

		void track(TRACKING_EVENT_NAME, trackingData);
		if (window) {
			window.open(recommendedGroup?.url, '_blank');
		}
	}, [context, recommendedGroup?.url, track, viewContext, window]);

	return {
		primaryAction,
		secondaryAction,
	};
}
